// Канонический клиент подтверждений. До инбокса `decideApproval` жил внутри
// features/ai-chat — вместе с типами; теперь и чат, и инбокс берут их отсюда,
// чтобы решение принималось одинаково независимо от места.
import { apiGet, apiPatch } from '../../../shared/api-client/index.js';
import { entityListKey, type EntityListKey } from '../../../shared/api/entityQueryKeys.js';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
export type ApprovalKind = 'tool_call' | 'scenario_step' | 'contact_delete' | 'deal_delete';
export type ApprovalExecutionStatus =
  | 'requested'
  | 'awaiting_approval'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'cancelled'
  | 'executing'
  | 'succeeded'
  | 'failed'
  | 'not_executed';

export interface ApprovalExecutionOutcome {
  status: ApprovalExecutionStatus;
  errorCode: string | null;
  attempt: number | null;
}

export type ApprovalBulkEntity =
  | 'deal'
  | 'task'
  | 'note'
  | 'contact'
  | 'company'
  | 'calendar_event'
  | 'contact_interaction'
  | 'document'
  | 'template'
  | 'attachment';
export type ApprovalBulkItemOutcome =
  | 'pending'
  | 'executing'
  | 'succeeded'
  | 'failed'
  | 'stale'
  | 'skipped';

export interface ApprovalBulkExecutionAggregate {
  version: 'partial_durable_v1';
  entity: ApprovalBulkEntity;
  total: number;
  succeeded: number;
  failed: number;
  stale: number;
  skipped: number;
  unfinished: number;
  /** Server-redacted and capped at twenty entries; intentionally contains no record IDs. */
  sample: readonly Readonly<{
    entity: ApprovalBulkEntity;
    outcome: ApprovalBulkItemOutcome;
  }>[];
}

export type ApprovalPresentationValue = string | number | boolean | null | readonly string[];
export interface ApprovalPresentationChangeV1 {
  readonly fieldKey: string;
  readonly before?: ApprovalPresentationValue;
  readonly after?: ApprovalPresentationValue;
  readonly value?: ApprovalPresentationValue;
}
export interface ApprovalPresentationV1 {
  readonly version: 'approval_presentation_v1';
  readonly actionKey: string;
  readonly target: Readonly<{
    kind:
      | 'task'
      | 'note'
      | 'deal'
      | 'contact'
      | 'company'
      | 'project'
      | 'calendar_event'
      | 'contact_interaction'
      | 'document'
      | 'template'
      | 'attachment'
      | 'scenario'
      | 'memory'
      | 'import'
      | 'dashboard_widget'
      | 'bulk';
    label: string;
  }>;
  readonly changes: readonly ApprovalPresentationChangeV1[];
  readonly impact: Readonly<{
    kind: 'create' | 'update' | 'delete';
    destructive: boolean;
    downstream?: Readonly<Record<string, number>>;
  }>;
}

const CORE_APPROVAL_PRESENTATION_ACTIONS = {
  'crm.approvals.summary.create_task': ['task', 'create'],
  'crm.approvals.summary.update_task': ['task', 'update'],
  'crm.approvals.summary.delete_task': ['task', 'delete'],
  'crm.approvals.summary.create_note': ['note', 'create'],
  'crm.approvals.summary.update_note': ['note', 'update'],
  'crm.approvals.summary.delete_note': ['note', 'delete'],
  'crm.approvals.summary.create_deal': ['deal', 'create'],
  'crm.approvals.summary.update_deal': ['deal', 'update'],
  'crm.approvals.summary.delete_deal': ['deal', 'delete'],
  'crm.approvals.summary.update_deal_stage': ['deal', 'update'],
  'crm.approvals.summary.create_contact': ['contact', 'create'],
  'crm.approvals.summary.update_contact': ['contact', 'update'],
  'crm.approvals.summary.delete_contact': ['contact', 'delete'],
  'crm.approvals.summary.create_company': ['company', 'create'],
  'crm.approvals.summary.update_company': ['company', 'update'],
  'crm.approvals.summary.delete_company': ['company', 'delete'],
  'crm.approvals.summary.create_project': ['project', 'create'],
  'crm.approvals.summary.add_project_member': ['project', 'update'],
  'crm.approvals.summary.remove_project_member': ['project', 'update'],
  'crm.approvals.summary.update_project': ['project', 'update'],
  'crm.approvals.summary.delete_project': ['project', 'delete'],
} as const;

const APPROVAL_PRESENTATION_TARGET_KINDS = new Set([
  'task',
  'note',
  'deal',
  'contact',
  'company',
  'project',
  'calendar_event',
  'contact_interaction',
  'document',
  'template',
  'attachment',
  'scenario',
  'memory',
  'import',
  'dashboard_widget',
  'bulk',
] as const);

const APPROVAL_PRESENTATION_FIELDS = new Set([
  'title',
  'description',
  'status',
  // Server-owned bulk task selection and target status.
  'batchPosition',
  'taskSelection',
  'taskStatus',
  'priority',
  'dueAt',
  'body',
  'amount',
  'stage',
  'source',
  'nextActivityDue',
  'startDate',
  'dueDate',
  'firstName',
  'lastName',
  'email',
  'phone',
  'role',
  'notes',
  'tags',
  'name',
  'domain',
  'industry',
  'website',
  'reason',
  'type',
  'subject',
  'startsAt',
  'endsAt',
  'timezone',
  'location',
  'fileName',
  'key',
  'level',
  'valueText',
  'embedding',
  'importance',
  'entityType',
  'rows',
  // Relation fields emitted by the server relationChanges contract; values are
  // server-redacted human labels, so they are as safe as the fields above.
  'assignee',
  'owner',
  'links',
  'deal',
  'project',
  'task',
  'company',
  'contact',
  'document',
  'member',
]);

/** Предупреждение сверки «текст пользователя → payload» (server: payload-consistency). */
export interface ApprovalConsistencyWarning {
  field: string;
  signal: 'email' | 'phone' | 'date' | 'amount';
  kind: 'missing' | 'mismatch';
  expected: string;
  actual?: string;
}

export function parseApprovalConsistencyWarnings(
  value: unknown,
): ApprovalConsistencyWarning[] | null {
  if (!Array.isArray(value)) return null;
  const warnings: ApprovalConsistencyWarning[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
    const warning = raw as Record<string, unknown>;
    if (
      typeof warning.field !== 'string' ||
      !['email', 'phone', 'date', 'amount'].includes(String(warning.signal)) ||
      !['missing', 'mismatch'].includes(String(warning.kind)) ||
      typeof warning.expected !== 'string'
    ) {
      return null;
    }
    warnings.push({
      field: warning.field,
      signal: warning.signal as ApprovalConsistencyWarning['signal'],
      kind: warning.kind as ApprovalConsistencyWarning['kind'],
      expected: warning.expected,
      ...(typeof warning.actual === 'string' ? { actual: warning.actual } : {}),
    });
  }
  return warnings;
}

export type ApprovalExecutionResult =
  | Readonly<{ taskId: string; title: string; updatedAt: string }>
  | Readonly<{ contactId: string; firstName?: string; lastName?: string; updatedAt?: string }>
  | Readonly<{ dealId: string; title?: string; updatedAt?: string }>;

export interface ApprovalItem {
  id: string;
  kind: ApprovalKind;
  status: ApprovalStatus;
  payload: Record<string, unknown>;
  expiresAt: string;
  createdAt?: string;
  decidedAt?: string | null;
  conversationId?: string | null;
  scenarioRunId?: string | null;
  /** Additive outcome projection; absent only on older servers/messages. */
  executionOutcome?: ApprovalExecutionOutcome;
  /** Explicit server projection; null for non-bulk, malformed, or unavailable results. */
  bulkExecutionAggregate?: ApprovalBulkExecutionAggregate | null;
  executionResult?: ApprovalExecutionResult | null;
  /** Server-owned, redacted human-readable projection. */
  presentation?: ApprovalPresentationV1 | null;
}

export function isApprovalPresentationV1(value: unknown): value is ApprovalPresentationV1 {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const target = record.target;
  if (
    record.version !== 'approval_presentation_v1' ||
    typeof record.actionKey !== 'string' ||
    !/^crm\.approvals\.(?:bulk\.)?summary\.[a-z0-9_]+$/u.test(record.actionKey) ||
    typeof target !== 'object' ||
    target === null ||
    Array.isArray(target)
  ) {
    return false;
  }
  const targetRecord = target as Record<string, unknown>;
  if (
    record.actionKey === 'crm.approvals.summary.add_tags' &&
    (!['contact', 'deal', 'company'].includes(String(targetRecord.kind)) ||
      typeof record.impact !== 'object' ||
      record.impact === null ||
      Array.isArray(record.impact) ||
      (record.impact as Record<string, unknown>)['kind'] !== 'update')
  )
    return false;
  const expected =
    CORE_APPROVAL_PRESENTATION_ACTIONS[
      record.actionKey as keyof typeof CORE_APPROVAL_PRESENTATION_ACTIONS
    ];
  if (
    typeof targetRecord.kind !== 'string' ||
    !APPROVAL_PRESENTATION_TARGET_KINDS.has(targetRecord.kind as never) ||
    (expected !== undefined && targetRecord.kind !== expected[0]) ||
    typeof targetRecord.label !== 'string' ||
    targetRecord.label.trim() === ''
  ) {
    return false;
  }
  if (
    !Array.isArray(record.changes) ||
    typeof record.impact !== 'object' ||
    record.impact === null
  ) {
    return false;
  }
  for (const change of record.changes) {
    if (typeof change !== 'object' || change === null || Array.isArray(change)) return false;
    const item = change as Record<string, unknown>;
    if (
      typeof item.fieldKey !== 'string' ||
      /ids?/iu.test(item.fieldKey) ||
      !APPROVAL_PRESENTATION_FIELDS.has(item.fieldKey)
    ) {
      return false;
    }
    for (const key of ['before', 'after', 'value']) {
      const entry = item[key];
      if (
        entry !== undefined &&
        entry !== null &&
        typeof entry !== 'string' &&
        typeof entry !== 'number' &&
        typeof entry !== 'boolean' &&
        !(Array.isArray(entry) && entry.every((part) => typeof part === 'string'))
      ) {
        return false;
      }
    }
  }
  const impact = record.impact as Record<string, unknown>;
  if (
    !['create', 'update', 'delete'].includes(String(impact.kind)) ||
    typeof impact.destructive !== 'boolean' ||
    (expected !== undefined && impact.kind !== expected[1]) ||
    (impact.kind === 'delete' && impact.destructive !== true)
  ) {
    return false;
  }
  if (impact.downstream !== undefined) {
    if (
      typeof impact.downstream !== 'object' ||
      impact.downstream === null ||
      Array.isArray(impact.downstream)
    ) {
      return false;
    }
    for (const [key, count] of Object.entries(impact.downstream as Record<string, unknown>)) {
      if (/ids?/iu.test(key) || !Number.isSafeInteger(count) || (count as number) < 0) return false;
    }
  }
  return true;
}

export interface ListApprovalsFilters {
  /** `all` — вся история; по умолчанию сервер отдаёт только ожидающие. */
  status?: ApprovalStatus | 'all';
}

export interface DecideApprovalInput {
  approvalId: string;
  decision: 'approve' | 'reject' | 'expire' | 'cancel';
}

export function getApprovalsQueryKey(
  workspaceId: string,
  filters: ListApprovalsFilters = {},
): EntityListKey {
  return entityListKey('approvals', workspaceId, { ...filters });
}

export async function listApprovals(filters: ListApprovalsFilters = {}): Promise<ApprovalItem[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  const query = params.toString();
  const items = await apiGet<ApprovalItem[]>(`/approvals${query ? `?${query}` : ''}`);
  return items.map(sanitizeApprovalItem);
}

export async function getApprovalById(approvalId: string): Promise<ApprovalItem> {
  return sanitizeApprovalItem(await apiGet<ApprovalItem>(`/approvals/${approvalId}`));
}

export async function decideApproval(input: DecideApprovalInput): Promise<ApprovalItem> {
  return sanitizeApprovalItem(
    await apiPatch<ApprovalItem, { decision: DecideApprovalInput['decision'] }>(
      `/approvals/${input.approvalId}`,
      { decision: input.decision },
    ),
  );
}

function sanitizeApprovalItem(item: ApprovalItem): ApprovalItem {
  return {
    ...item,
    presentation: isApprovalPresentationV1(item.presentation) ? item.presentation : null,
  };
}
